import { Button, Modal, Textarea, useToast } from '@/components';
import { FadGroupPicker } from '@/features/release-config/FadGroupPicker';
import { celebrate } from '@/stores/welcome.store';
import type { ReleaseDetail } from '@uchat-team-management/shared';
import { ApiClientError, generateReleaseNotes } from '@uchat-team-management/shared';
import { Show, createSignal } from 'solid-js';
import { useDistributeRelease, useReleaseChangelog } from './hooks';

/**
 * Publish to testers on Firebase App Distribution (UCH-418). Groups are picked
 * from the FAD-synced options only (UCH-513), pre-checked from the channel's
 * defaults; release notes prefill from the frozen changelog and stay editable.
 */
export const DistributeModal = (props: {
  open: boolean;
  onClose: () => void;
  release: ReleaseDetail;
  defaultGroups: string[];
}) => {
  const toast = useToast();
  const distribute = useDistributeRelease(props.release.id);
  const changelog = useReleaseChangelog(() => ({
    releaseId: props.release.id,
    against: 'baseline',
  }));

  const [groupsDraft, setGroupsDraft] = createSignal<string[] | null>(null);
  const [notesDraft, setNotesDraft] = createSignal<string | null>(null);
  const groupAliases = () => groupsDraft() ?? props.defaultGroups;
  const notes = () =>
    notesDraft() ?? (changelog.data ? generateReleaseNotes(changelog.data.data) : '');

  const send = async () => {
    try {
      const { data } = await distribute.mutateAsync({
        groupAliases: groupAliases(),
        releaseNotes: notes(),
      });
      toast.success(`Published to testers (${data.distributed.map((d) => d.platform).join(', ')})`);
      celebrate();
      props.onClose();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.error.message : 'Request failed');
    }
  };

  return (
    <Modal open={props.open} onClose={props.onClose} title="Publish to testers" size="lg">
      <div class="flex flex-col gap-3">
        <p class="text-sm text-neutral-500">
          Distributes build #{props.release.buildNumber} ({props.release.version}) on Firebase App
          Distribution to the tester groups below.
        </p>
        <FadGroupPicker
          productId={props.release.productId}
          environment={props.release.environment}
          value={groupAliases()}
          onChange={setGroupsDraft}
          label="Tester groups"
        />
        <Textarea
          label="Release notes"
          rows={8}
          value={notes()}
          onInput={(e) => setNotesDraft(e.currentTarget.value)}
        />
        <div class="flex justify-end gap-2">
          <Button variant="secondary" onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            onClick={send}
            loading={distribute.isPending}
            disabled={groupAliases().length === 0}
          >
            Send to testers
          </Button>
        </div>
        <Show when={changelog.isPending}>
          <p class="text-xs text-neutral-500">Loading change log for release notes…</p>
        </Show>
      </div>
    </Modal>
  );
};
